import type { DashboardData, ModuleKey, WorldItem } from "../types";
import { seedDashboard } from "./seed";

const DEMO_KEY = "nehalxaman-demo-state";

function demoData(): DashboardData {
  const stored = localStorage.getItem(DEMO_KEY);
  if (!stored) return structuredClone(seedDashboard);
  try { return JSON.parse(stored) as DashboardData; } catch { return structuredClone(seedDashboard); }
}

function storeDemo(data: DashboardData) {
  localStorage.setItem(DEMO_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent("nehalxaman:demo-change"));
  return data;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
    credentials: "include",
  });
  const failure = !response.ok ? await response.json<{ error?: string }>().catch(() => null) : null;
  if (!response.ok) throw new Error(failure?.error ?? `Request failed (${response.status})`);
  return response.json() as Promise<T>;
}

export async function getDashboard(): Promise<DashboardData> {
  try { return await request<DashboardData>("/api/v1/dashboard"); }
  catch (error) {
    if (import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === "true") return demoData();
    throw error;
  }
}

export async function updateMeter(value: number, note: string, source: string): Promise<DashboardData> {
  try { await request("/api/v1/meter", { method: "PATCH", body: JSON.stringify({ value, note, source }) }); return getDashboard(); }
  catch (error) {
    if (!(import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === "true")) throw error;
    const data = demoData();
    const from = data.meter.value;
    data.meter.value = value;
    data.meter.updatedAt = new Date().toISOString();
    data.meter.history.unshift({ id: crypto.randomUUID(), from, to: value, note, source, actor: data.user, createdAt: data.meter.updatedAt });
    data.activity.unshift({ id: crypto.randomUUID(), actor: data.user, action: "tuned the Affinity Core", subject: `${from} → ${value}`, icon: "◉", createdAt: data.meter.updatedAt });
    return storeDemo(data);
  }
}

export async function createItem(module: ModuleKey, input: Pick<WorldItem, "title" | "description" | "status" | "date">): Promise<DashboardData> {
  try { await request(`/api/v1/items/${module}`, { method: "POST", body: JSON.stringify(input) }); return getDashboard(); }
  catch (error) {
    if (!(import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === "true")) throw error;
    const data = demoData();
    const createdAt = new Date().toISOString();
    const symbols: Record<ModuleKey, string> = { tasks: "◆", trips: "▲", shopping: "▦", photos: "▧", notes: "✦", dreams: "◇", wishlist: "□", dates: "◈", memories: "⌁" };
    data.items.unshift({ id: crypto.randomUUID(), module, ...input, icon: symbols[module], accent: "cyan", author: data.user, createdAt, updatedAt: createdAt, version: 1 });
    data.activity.unshift({ id: crypto.randomUUID(), actor: data.user, action: `added to ${module}`, subject: input.title, icon: symbols[module], createdAt });
    return storeDemo(data);
  }
}

export async function toggleItem(item: WorldItem): Promise<DashboardData> {
  const next = item.status === "completed" ? "active" : "completed";
  try { await request(`/api/v1/items/${item.module}/${item.id}`, { method: "PATCH", body: JSON.stringify({ status: next, version: item.version }) }); return getDashboard(); }
  catch (error) {
    if (!(import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === "true")) throw error;
    const data = demoData();
    data.items = data.items.map((candidate) => candidate.id === item.id ? { ...candidate, status: next, version: candidate.version + 1, updatedAt: new Date().toISOString() } : candidate);
    return storeDemo(data);
  }
}

export async function createPartnerInvite(): Promise<{ inviteUrl: string; expiresAt: string }> {
  return request("/api/v1/admin/invites", { method: "POST", body: "{}" });
}
